import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cmsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

const AdminHomepageContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    hero_title: '',
    hero_tagline: '',
    hero_description: '',
    hero_primary_cta_label: '',
    hero_primary_cta_url: '',
    hero_secondary_cta_label: '',
    hero_secondary_cta_url: '',
    articles_mode: 'latest',
    manual_slugs: [],
    newsletter_banner_title: '',
    newsletter_banner_body: '',
    newsletter_banner_cta_label: '',
    newsletter_banner_cta_url: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await cmsApi.getHomepage();
        setData(res);
      } catch (error) {
        console.error('Failed to load homepage CMS content:', error);
        toast.error('Anasayfa i\u00e7eri\u011fi y\u00fcklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleManualSlugsChange = (value) => {
    const slugs = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setData((prev) => ({ ...prev, manual_slugs: slugs }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cmsApi.updateHomepage(data);
      toast.success('Anasayfa i\u00e7eri\u011fi g\u00fcncellendi');
    } catch (error) {
      console.error('Failed to save homepage content:', error);
      toast.error('Anasayfa i\u00e7eri\u011fi kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-foreground">Anasayfa \u0130\u00e7eri\u011fi</h1>
          <p className="text-sm text-muted-foreground">
            Hero ba\u015fl\u0131\u011f\u0131, a\u00e7\u0131klamalar ve CTA butonlar\u0131n\u0131 buradan y\u00f6net.
          </p>
        </div>
        <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hero */}
        <Card>
          <CardHeader>
            <CardTitle>Hero</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hero_title">Ba\u015fl\u0131k</Label>
              <Input
                id="hero_title"
                value={data.hero_title}
                onChange={(e) => handleChange('hero_title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_tagline">Tagline</Label>
              <Input
                id="hero_tagline"
                value={data.hero_tagline}
                onChange={(e) => handleChange('hero_tagline', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hero_description">A\u00e7\u0131klama</Label>
              <Textarea
                id="hero_description"
                value={data.hero_description}
                onChange={(e) => handleChange('hero_description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Hero CTA'lar */}
        <Card>
          <CardHeader>
            <CardTitle>CTA Butonlar\u0131</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Birincil CTA Metni</Label>
              <Input
                value={data.hero_primary_cta_label}
                onChange={(e) => handleChange('hero_primary_cta_label', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Birincil CTA URL</Label>
              <Input
                value={data.hero_primary_cta_url}
                onChange={(e) => handleChange('hero_primary_cta_url', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>7incil CTA Metni</Label>
              <Input
                value={data.hero_secondary_cta_label}
                onChange={(e) => handleChange('hero_secondary_cta_label', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>7incil CTA URL</Label>
              <Input
                value={data.hero_secondary_cta_url}
                onChange={(e) => handleChange('hero_secondary_cta_url', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Yaz\u0131 Listesi Ayarlar\u0131 */}
      <Card>
        <CardHeader>
          <CardTitle>Anasayfa Yaz\u0131lar\u0131</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-xs">
            <Label>G\u00f6r\u00fcn\u00fcm Modu</Label>
            <Select
              value={data.articles_mode}
              onValueChange={(value) => handleChange('articles_mode', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">En Yeni Yaz\u0131lar</SelectItem>
                <SelectItem value="featured">\u00d6ne \u00c7\u0131kanlar</SelectItem>
                <SelectItem value="manual">Manuel Liste</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {data.articles_mode === 'manual' && (
            <div className="space-y-2">
              <Label>Manuel Slug Listesi</Label>
              <Textarea
                placeholder="slug-1, slug-2, slug-3"
                value={data.manual_slugs.join(', ')}
                onChange={(e) => handleManualSlugsChange(e.target.value)}
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Slug&apos;lar\u0131 virg\u00fcl ile ay\u0131r\u0131n. S\u0131ra, girdi\u011finiz s\u0131raya g\u00f6re g\u00f6r\u00fcnt\u00fclenir.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Newsletter Banner */}
      <Card>
        <CardHeader>
          <CardTitle>Alt B\u00fclten Banner\u0131</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-lg">
            <Label>Ba\u015fl\u0131k</Label>
            <Input
              value={data.newsletter_banner_title}
              onChange={(e) => handleChange('newsletter_banner_title', e.target.value)}
            />
          </div>
          <div className="space-y-2 max-w-2xl">
            <Label>A\u00e7\u0131klama</Label>
            <Textarea
              value={data.newsletter_banner_body}
              onChange={(e) => handleChange('newsletter_banner_body', e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2 max-w-lg">
            <Label>CTA Metni</Label>
            <Input
              value={data.newsletter_banner_cta_label}
              onChange={(e) => handleChange('newsletter_banner_cta_label', e.target.value)}
            />
          </div>
          <div className="space-y-2 max-w-lg">
            <Label>CTA URL</Label>
            <Input
              value={data.newsletter_banner_cta_url}
              onChange={(e) => handleChange('newsletter_banner_cta_url', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default AdminHomepageContent;
